import { useCallback, useMemo } from "react";

import { v4 as uuid } from "uuid";

import { metrics } from "@north-beam/nb-common";

import { useFormula } from "../atoms";

const formulaEvaluator = new metrics.FormulaEvaluator("");

export const useFormulaBuilder = () => {
  const [formula, setFormula] = useFormula();
  const updateFormula = useCallback(
    (newFormula: metrics.Token[]) => {
      setFormula(newFormula);
      formulaEvaluator.setFormula(
        newFormula
          .map(({ type, value }) => {
            if (
              [metrics.NODE_TYPE.VARIABLE, metrics.NODE_TYPE.NUMBER].includes(
                type,
              )
            ) {
              return `{${value}}`;
            }
            return value;
          })
          .join(" "),
      );
    },
    [setFormula],
  );

  const addNumberOperand = useCallback(
    (value) => {
      const newArray = [
        ...formula,
        { value, id: uuid(), type: metrics.NODE_TYPE.NUMBER },
      ];
      updateFormula(newArray);
    },
    [formula, updateFormula],
  );

  const addVariableOperand = useCallback(
    (value) => {
      const newArray = [
        ...formula,
        { value, id: uuid(), type: metrics.NODE_TYPE.VARIABLE },
      ];
      updateFormula(newArray);
    },
    [formula, updateFormula],
  );

  const addOperator = useCallback(
    (value) => {
      const newArray = [
        ...formula,
        { value, id: uuid(), type: metrics.NODE_TYPE.OPERATOR },
      ];
      updateFormula(newArray);
    },
    [formula, updateFormula],
  );

  const remove = useCallback(
    (id) => {
      const index = formula.findIndex((item) => item.id === id);
      if (index !== -1) {
        const newArray = [...formula];
        newArray.splice(index, 1);
        updateFormula(newArray);
      }
    },
    [formula, updateFormula],
  );

  const reorder = useCallback(
    (activeId, overId) => {
      if (activeId === overId) {
        return;
      }

      const startIndex = formula.findIndex((item) => item.id === activeId);
      const endIndex = formula.findIndex((item) => item.id === overId);
      if (startIndex !== -1 && endIndex !== -1) {
        const newArray = [...formula];
        const [removed] = newArray.splice(startIndex, 1);
        newArray.splice(endIndex, 0, removed);

        updateFormula(newArray);
      }
    },
    [formula, updateFormula],
  );

  const clear = useCallback(() => {
    updateFormula([]);
  }, [updateFormula]);

  const selectedMetricIds = useMemo(
    () => [
      ...new Set(
        formula
          .filter(({ type }) => type === metrics.NODE_TYPE.VARIABLE)
          .map(({ value }) => value),
      ),
    ],
    [formula],
  );

  const isFormulaValid = useMemo(
    () => formula.length === 0 || formulaEvaluator.isValid(),
    [formula],
  );

  return {
    formula,
    selectedMetricIds,
    updateFormula,
    addNumberOperand,
    addVariableOperand,
    addOperator,
    remove,
    reorder,
    clear,
    formulaEvaluator,
    isFormulaValid,
  };
};
